import React, { ReactNode } from 'react';
import { MdUndo } from 'react-icons/md';
import { IoReorderThree } from 'react-icons/io5';

import { Order, ReorderCellWrapper } from '../styles';
import { VdpItem } from 'lib/api';
import { STANDARD_DATE_FORMAT } from 'lib/const/DateFormat';
import dayjs from 'dayjs';
import { VDP_IntegrationTypes } from 'lib/const/vdp';
import { getDisplayName } from 'lib/utils/functions';
import { Button } from 'react-covideo-common';
import { FcCheckmark } from 'react-icons/fc';

export type TableRow = {
  id: number;
  videoTitle: string;
  recordedBy: string;
  pushedBy: string;
  vin: string;
  createdAt: string;
  vehicleTitle: string;
  buttons: ReactNode;
  reorder?: ReactNode;
  videoId: number;
  integrationType: string;
};

type TableColumn = {
  value: string;
  label: string;
  padding: string;
  sortable: boolean;
  align?: string;
};

const MAX_VDP_ALLOWED = 0;

type CellButtonsProps = {
  handlePullClick: () => void;
  t: Function;
};

const CellButtons = ({ handlePullClick, t }: CellButtonsProps) => {
  return (
    <Button
      variant='destructive'
      icon={<MdUndo />}
      onClick={handlePullClick}
      text={t('Pull')}
    />
  );
};

type ReorderProps = {
  order: number;
  setTooltipIndex: (arg1: number) => void;
  color: string;
};
const CellReorder = ({ order, setTooltipIndex, color }: ReorderProps) => {
  return (
    <ReorderCellWrapper
      onMouseOver={() => setTooltipIndex(order - 1)}
      onMouseOut={() => setTooltipIndex(-1)}
    >
      <IoReorderThree color={color} size={24} />
      <Order>{order}</Order>
      <FcCheckmark
        style={{ opacity: MAX_VDP_ALLOWED && order <= MAX_VDP_ALLOWED ? 1 : 0 }}
      />
    </ReorderCellWrapper>
  );
};
export const getTableCols = (t: Function): TableColumn[] => [
  {
    value: 'reorder',
    label: t('#'),
    padding: '0 8px 0 0',
    sortable: false,
    align: 'center',
  },
  {
    value: 'videoTitle',
    label: t('Video Title'),
    padding: '0 8px 0 0',
    sortable: false,
  },
  {
    value: 'recordedBy',
    label: t('Recorded by'),
    padding: '0 8px 0 0',
    sortable: false,
  },
  {
    value: 'vin',
    label: t('VIN'),
    padding: '0 8px 0 0',
    sortable: false,
  },
  {
    value: 'createdAt',
    label: t('Pushed on'),
    padding: '0 8px 0 0',
    sortable: true,
  },
  {
    value: 'integrationType',
    label: t('Destination'),
    padding: '0',
    sortable: false,
  },
  {
    value: 'buttons',
    label: '',
    padding: '0',
    sortable: false,
  },
];

export const formatTableRows = (
  vdps: VdpItem[],
  setTooltipIndex: (arg: number) => void,
  setVdpToPull: (vdp: VdpItem) => void,
  t: Function,
  color: string
): TableRow[] =>
  vdps.map((vdp, index) => ({
    id: vdp.id,
    videoTitle: vdp?.video?.title || '',
    recordedBy: vdp?.video?.user
      ? `${vdp.video.user.firstName || ''} ${vdp.video.user.lastName || ''}`
      : '',
    pushedBy: vdp?.user
      ? `${vdp.user.firstName || ''} ${vdp.user.lastName || ''}`
      : '',
    vin: vdp?.vin || '',
    vehicleTitle: vdp?.inventoryItem
      ? getDisplayName([
          vdp.inventoryItem.make,
          vdp.inventoryItem.model,
          vdp.inventoryItem.year,
        ])
      : '',
    createdAt: vdp?.createdAt
      ? dayjs(vdp.createdAt).format(`${STANDARD_DATE_FORMAT} HH:mm A`)
      : '',
    buttons: <CellButtons handlePullClick={() => setVdpToPull(vdp)} t={t} />,
    reorder: (
      <CellReorder
        order={index + 1}
        setTooltipIndex={setTooltipIndex}
        color={color}
      />
    ),
    videoId: vdp.videoId,
    integrationType: VDP_IntegrationTypes[vdp.integrationType]?.label || '',
  }));
